import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { navigateToTop } from "../../utils/Constants";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import { API_BASE } from "../../config/apiBase";
import "../../styles/pages/content/_administration.scss";

// ─── Card data ────────────────────────────────────────────────────────────────
const ADMIN_SECTIONS = [
    {
        id: "users",
        icon: "users",
        label: "User management",
        desc: "View registered users, filter by plan, manage roles, and manually adjust subscriptions.",
        href: "/administration/users",
        accent: "purple",
        status: null,
    },
    {
        id: "security",
        icon: "shield",
        label: "Security & compliance",
        desc: "Audit logs, data protection settings, and organisation-wide policy controls.",
        href: "/administration/security",
        accent: "blue",
        status: null,
    },
    // {
    //     id: "support",
    //     icon: "headset",
    //     label: "Support workflows",
    //     desc: "Manage support escalations, internal tooling, and team enablement resources.",
    //     href: "/administration/support",
    //     accent: "green",
    //     status: null,
    // },
    // {
    //     id: "flags",
    //     icon: "flag",
    //     label: "Feature flags",
    //     desc: "Toggle features per user or globally. Roll out beta changes to a subset of users safely.",
    //     href: "/administration/flags",
    //     accent: "orange",
    //     status: "beta",
    // },
    {
        id: "analytics",
        icon: "bar-chart",
        label: "Analytics",
        desc: "Active users, conversion rates, most-practised questions, and churn indicators.",
        href: "/administration/analytics",
        accent: "purple",
        status: null,
    },
    // {
    //     id: "comms",
    //     icon: "mail",
    //     label: "Communications",
    //     desc: "Send targeted emails to user segments. Re-engage inactive accounts.",
    //     href: "/administration/comms",
    //     accent: "blue",
    //     status: "soon",
    // },
];

// ─── Quick stats ──────────────────────────────────────────────────────────────
const formatNumber = (value) =>
    value == null ? "—" : Number(value).toLocaleString();
const formatPercent = (value) =>
    value == null ? "—" : `${Number(value).toFixed(1)}%`;

// ─── Main component ───────────────────────────────────────────────────────────
export default function Administration() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyticsForbidden, setAnalyticsForbidden] = useState(false);

    const apiBase = API_BASE;

    const authedFetch = useCallback(
        async (path, options = {}) => {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            return fetch(`${apiBase}${path}`, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        [apiBase, getAccessTokenSilently]
    );

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }
        let aborted = false;
        (async () => {
            try {
                setLoading(true);
                setAnalyticsForbidden(false);
                const res = await authedFetch("/admin/analytics", {
                    method: "GET",
                });
                if (!res.ok) {
                    if (res.status === 401 || res.status === 403) {
                        if (!aborted) setAnalyticsForbidden(true);
                        return;
                    }
                    throw new Error("Failed to load analytics");
                }
                const data = await res.json();
                if (!aborted) setAnalytics(data);
            } catch {
                if (!aborted) setAnalytics(null);
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [authedFetch, isAuthenticated, isLoading]);

    const stats = useMemo(() => {
        if (loading) {
            return [
                { label: "Total users", value: "…" },
                { label: "Recording conversion", value: "…" },
                { label: "Recordings (30d)", value: "…" },
                { label: "Pending errors", value: "…" },
            ];
        }
        return [
            {
                label: "Total users",
                value: formatNumber(analytics?.totals?.total_users),
            },
            {
                label: "Recording conversion",
                value: formatPercent(
                    analytics?.totals?.first_recording_conversion_rate
                ),
            },
            {
                label: "Recordings (30d)",
                value: formatNumber(analytics?.totals?.recordings_30d),
            },
            {
                label: "Pending errors",
                value: formatNumber(analytics?.processing_health?.error_count),
            },
        ];
    }, [analytics, loading]);

    return (
        <div className="adm" id="administration">
            <div className="adm__inner">
                <div>
                    <Breadcrumbs
                        items={[
                            { label: "Home", href: "/" },
                            {
                                label: "Administration",
                                href: "/administration",
                            },
                        ]}
                    />

                    {/* ── Header ── */}
                    <div className="adm__header">
                        <div className="adm__header-text">
                            <h1 className="adm__title">Dashboard</h1>
                            <p className="adm__sub">
                                Manage users, security, and platform settings
                                for Mooz.
                            </p>
                        </div>
                        {/* <div className="adm__badge" aria-label="Admin access only">
                        <span
                            className="adm-icon adm-icon--shield"
                            aria-hidden="true"
                        />
                        Admin only
                    </div> */}
                    </div>
                </div>

                {/* ── Quick stats ── */}
                {!analyticsForbidden && analytics && (
                    <div className="adm__stats" aria-label="Platform overview">
                        {stats.map(({ label, value }) => (
                            <div className="adm__stat" key={label}>
                                <span className="adm__stat-value">{value}</span>
                                <span className="adm__stat-label">{label}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Section grid ── */}
                <div className="adm__grid">
                    {ADMIN_SECTIONS.map(
                        (
                            { id, icon, label, desc, href, accent, status },
                            i
                        ) => (
                            <Link
                                key={id}
                                to={href}
                                className={`adm-card adm-card--${accent}`}
                                style={{ "--card-index": i }}
                                aria-label={label}
                                onClick={navigateToTop}
                            >
                                <div className="adm-card__top">
                                    <div
                                        className="adm-card__icon"
                                        aria-hidden="true"
                                    >
                                        <span
                                            className={`adm-icon adm-icon--${icon}`}
                                            aria-hidden="true"
                                        />
                                    </div>
                                    {status && (
                                        <span
                                            className={`adm-card__status adm-card__status--${status}`}
                                        >
                                            {status === "beta"
                                                ? "Beta"
                                                : "Coming soon"}
                                        </span>
                                    )}
                                </div>
                                <h2 className="adm-card__label">{label}</h2>
                                <p className="adm-card__desc">{desc}</p>
                                <div className="adm-card__footer">
                                    <span className="adm-card__cta">
                                        Open{" "}
                                        <span
                                            className="adm-icon adm-icon--arrow"
                                            aria-hidden="true"
                                        />
                                    </span>
                                </div>
                            </Link>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
